import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import axios from '@axios';
import { RootState } from '@redux/reducers';
import { Paginated } from '@models/paginated';
import { toQueryParameters } from 'src/extensions/object';
import { PaginatedResponseApi } from '@models/order/items/response';
import { commercialBonusesSalesReportColumnsTranslations } from '@utils/translation';
import {
    CommercialBonusSaleResponse,
    CommercialBonusSaleResponseApi,
    ReportRequest,
} from '@models/report';
import { exportReport } from '@utils/file';
import { translate } from '@components/i18n';

export const fetchCommercialBonusesSales = createAsyncThunk<
    Paginated<CommercialBonusSaleResponse>,
    ReportRequest
>(
    'commercial-bonuses-sales/fetchCommercialBonusesSales',
    async (request, thunkAPI) => {
        try {
            const state = thunkAPI.getState() as RootState;
            const { commercialBonusesSales } = state;
            const { request: oldRequest } = commercialBonusesSales;

            thunkAPI.dispatch(updateRequestCommercialBonusSale(request));

            const params = generateParams(request, oldRequest);

            const response = await axios.get<
                PaginatedResponseApi<CommercialBonusSaleResponseApi>
            >(`/relatorio/pedidosbonificacao?${params}`);
            const { data: dataResponse, status: statusResponse } = response;

            if (!dataResponse) {
                return {
                    items: [],
                    current: 0,
                    total: 0,
                    pageSize: 0,
                };
            } else {
                const result = dataResponse.content.map((u) =>
                    Object.assign(new CommercialBonusSaleResponseApi(), u),
                );

                if (statusResponse === 200) {
                    return {
                        items: result.map((u) =>
                            u.toCommercialBonusSaleResponse(),
                        ),
                        current: request.current,
                        total: response.data.totalElements,
                        pageSize: request.pageSize,
                    };
                } else {
                    return thunkAPI.rejectWithValue('');
                }
            }
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const exportCommercialBonusesSales = createAsyncThunk(
    'commercial-bonuses-sales/exportCommercialBonusesSales',
    async (request: ReportRequest, thunkAPI) => {
        try {
            const state = thunkAPI.getState() as RootState;
            const { approvedOrders } = state;
            const { request: oldRequest } = approvedOrders;

            const params = generateParams(request, oldRequest);

            const response = await axios.get(
                `/relatorio/pedidosbonificacao/export?${params}`,
                { responseType: 'blob' },
            );
            exportReport(
                response.data,
                `${translate('general.reportComercialSalesBonification')}.xlsx`,
            );
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return thunkAPI.rejectWithValue(message);
        }
    },
);

const generateParams = (request: ReportRequest, oldRequest: ReportRequest) => {
    const { filterValue, filterBy } = oldRequest;

    if (filterValue != request.filterValue || filterBy != request.filterBy)
        request.current = 1;

    let parameters: any = {
        page: request.current - 1,
        size: request.pageSize,
        sort: request.field
            ? `${
                  commercialBonusesSalesReportColumnsTranslations[
                      request.field
                  ] || ''
              },${request.order === 'ascend' ? 'asc' : 'desc'}`
            : '',
    };

    parameters =
        request.filterValue && request.filterBy && request.comparisonType
            ? {
                  ...parameters,
                  filterValue: request.filterValue,
                  filterType: request.filterBy?.toUpperCase(),
                  comparisonType: request.comparisonType?.toUpperCase(),
              }
            : parameters;

    return toQueryParameters(parameters);
};

interface ICommercialBonusSaleSelectorState {
    commercialBonusSale: CommercialBonusSaleResponse;
    commercialBonusesSales: CommercialBonusSaleResponse[];
    request: ReportRequest;
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    errorMessage: string;
}

const initialState: ICommercialBonusSaleSelectorState = {
    commercialBonusSale: undefined,
    commercialBonusesSales: [],
    request: {
        filterBy: '',
        filterValue: '',
        comparisonType: '',
        status: '',
        pageSize: 10,
        current: 1,
        total: 0,
    },
    isFetching: true,
    isSuccess: false,
    isError: false,
    errorMessage: '',
};

const commercialBonusesSalesSlice = createSlice({
    name: 'commercialBonusesSalesSlice',
    initialState,
    reducers: {
        commercialBonusesSales: (
            state,
            action: PayloadAction<CommercialBonusSaleResponse>,
        ) => {
            state.commercialBonusSale = action.payload;

            return state;
        },
        updateRequestCommercialBonusSale: (
            state,
            action: PayloadAction<ReportRequest>,
        ) => {
            state.request = action.payload;

            return state;
        },
        resetCommercialBonusSaleState: (state) => {
            state = { ...initialState };
            return state;
        },
    },
    extraReducers: {
        [fetchCommercialBonusesSales.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<Paginated<CommercialBonusSaleResponse>>,
        ) => {
            state.commercialBonusesSales = [...payload.items];
            state.request = {
                ...state.request,
                total: payload.total,
                pageSize: payload.pageSize,
                current: payload.current,
            };
            state.isFetching = false;
            state.isSuccess = true;
            return state;
        },
        [fetchCommercialBonusesSales.rejected.toString()]: (state, action) => {
            state.isFetching = false;
            state.isError = true;
            state.errorMessage = action.payload;
        },
        [fetchCommercialBonusesSales.pending.toString()]: (state) => {
            state.isFetching = true;
            state.isError = false;
        },
    },
});

export const commercialBonusSaleSelector = (
    state: RootState,
): ICommercialBonusSaleSelectorState => state.commercialBonusesSales;

export const {
    commercialBonusesSales,
    updateRequestCommercialBonusSale,
    resetCommercialBonusSaleState,
} = commercialBonusesSalesSlice.actions;

export default commercialBonusesSalesSlice.reducer;
